import axios from "axios";

// Same-origin API base. The frontend is served from the same host as the
// backend's /api routes (the Emergent ingress proxies /api/* to the FastAPI
// service on every host — preview, *.emergent.host, and the production custom
// domain). Using a relative URL keeps every request first-party, which:
//   1. avoids cross-origin cookie-blocking (third-party cookies are blocked
//      by default in incognito on Chrome/Edge/Safari since 2024),
//   2. eliminates CORS preflight overhead on every request,
//   3. removes the bake-in dependency on REACT_APP_BACKEND_URL at build time,
//      which on a misconfigured production deploy would point at the wrong
//      host and silently break login in fresh browser sessions.
// REACT_APP_BACKEND_URL is intentionally NOT used here. It remains in .env
// so the platform-protected key isn't deleted, but the runtime path now
// always resolves against window.location.origin.
export const API = "/api";
export const api = axios.create({ baseURL: API, withCredentials: true });

/**
 * Extract a human-readable string from an axios error.
 *
 * Backend endpoints traditionally return `{ detail: "string" }` on errors, and
 * the codebase has ~30 places that toast `e?.response?.data?.detail` directly.
 * That works fine for string details — but breaks when an endpoint returns
 * `{ detail: { code, message, drifted: [...] } }` (richer structured errors).
 * `toast.error()` receives an object → React tries to render an object as a
 * child → throws "Objects are not valid as a React child" → the entire
 * containing component subtree unmounts (header + sidebar survive because they
 * live in a different subtree).
 *
 * This helper extracts a safe string in priority order:
 *   1) detail.message (our convention for structured errors)
 *   2) detail itself when it's a string
 *   3) error.message (axios/network errors)
 *   4) fallback supplied by the caller
 *
 * Use this everywhere `toast.error(e?.response?.data?.detail)` exists today.
 * Backwards-compatible: returns the same string for endpoints that still
 * return a plain string detail.
 */
export const apiErrorMessage = (err, fallback = "Failed") => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") return detail.message;
  if (typeof err?.message === "string" && err.message) return err.message;
  return fallback;
};

// Date helpers shared across rate-versioning UIs.
//
// `fmtIsoDateOnly(d)` renders a YYYY-MM-DD string from any datetime-ish
// input WITHOUT going through `new Date(...)` when the input is already
// ISO-prefixed. This avoids the local-time round-trip that produced the
// off-by-one bug for IST users (see iteration_27 RCA).
export const fmtIsoDateOnly = (d) => {
  if (!d) return "—";
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  try { return new Date(s).toISOString().slice(0, 10); } catch { return s.slice(0, 10); }
};

// Strict YYYY-MM-DD validator. Used to gate the "is the picked date in the
// future?" comparison in rate-change flows — string comparison only works
// if both sides are zero-padded ISO; this guard rejects malformed input
// so we never silently treat a typo as "today".
export const isYmd = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);

// Today as YYYY-MM-DD in the **user's local timezone**. Date pickers
// (`<input type="date">`) and most operators think in their local tz —
// using UTC here was the iter-28 bug that broke "today's MDR change":
// at 1 AM IST the UTC date is still YESTERDAY, so the comparison
// `picked > todayYmd()` flipped to true and the future-date branch
// SKIPPED the entity-scalar update. Switching to local components keeps
// "today" aligned with what the user sees in the picker.
export const todayYmd = () => {
  const d = new Date();
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

// Absolute URL helper — for downloads / window.open() / <a href>. Axios
// requests use the relative `baseURL` above (so cookies stay first-party),
// but `window.open("/api/...")` resolves against the document URL — fine
// for top-level navigation but NOT what we want for popup-style downloads.
// `apiUrl("/leads/sample-template")` → "https://current-host/api/leads/sample-template".
// Always pass the path WITHOUT the leading "/api" — this helper prepends it.
export const apiUrl = (path = "") => {
  const p = String(path || "");
  const norm = p.startsWith("/") ? p : `/${p}`;
  return `${window.location.origin}/api${norm}`;
};

// 401 interceptor with silent token refresh:
// - Access tokens expire after 10 min (short by design). A background
//   /auth/refresh call (uses the 7-day refresh_token cookie) swaps in a fresh
//   access cookie transparently so active users don't get logged out.
// - If the refresh fails (refresh_token expired or revoked), fall back to the
//   old behaviour: emit `auth:401` so AuthProvider routes back to /login.
// - Concurrent 401s share a single refresh promise to avoid stampeding the
//   /refresh endpoint when N parallel requests all 401 at the same time.
let _refreshPromise = null;
const _isAuthUrl = (u) => (u || "").includes("/auth/");

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err?.config;
    const status = err?.response?.status;
    if (status !== 401 || !original || original._retried || _isAuthUrl(original.url)) {
      if (status === 401 && !_isAuthUrl(original?.url || "")) {
        window.dispatchEvent(new CustomEvent("auth:401"));
      }
      return Promise.reject(err);
    }
    try {
      if (!_refreshPromise) {
        _refreshPromise = api
          .post("/auth/refresh")
          .finally(() => { _refreshPromise = null; });
      }
      await _refreshPromise;
      original._retried = true;
      return api(original);
    } catch (refreshErr) {
      window.dispatchEvent(new CustomEvent("auth:401"));
      return Promise.reject(err);
    }
  }
);

export const fmtINR = (n, decimals = 2) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const x = Number(n);
  return x.toLocaleString("en-IN", { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
};

export const fmtCompact = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const x = Math.abs(Number(n));
  const sign = n < 0 ? "-" : "";
  if (x >= 1e7) return `${sign}${(x / 1e7).toFixed(2)} Cr`;
  if (x >= 1e5) return `${sign}${(x / 1e5).toFixed(2)} L`;
  if (x >= 1e3) return `${sign}${(x / 1e3).toFixed(1)} K`;
  return `${sign}${x.toFixed(0)}`;
};

export const fmtPct = (p, digits = 2) => {
  if (p === null || p === undefined || isNaN(p)) return "—";
  return `${(Number(p) * 100).toFixed(digits)}%`;
};

export const fmtBps = (r) => {
  if (r === null || r === undefined || isNaN(r)) return "—";
  return `${(Number(r) * 100).toFixed(2)}%`;
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const fmtPeriod = (p) => {
  if (!p || typeof p !== "string") return p || "";
  const m = p.match(/^(\d{4})-(\d{2})$/);
  if (!m) return p;
  const idx = parseInt(m[2], 10) - 1;
  if (idx < 0 || idx > 11) return p;
  return `${MONTHS[idx]} ${m[1]}`;
};
export const periodOptions = (periods) => (periods || []).map((p) => ({ value: p, label: fmtPeriod(p) }));

// Precision-safe rate helpers — IEEE 754 floats lose precision on `* 100` /
// `/ 100` round-trips (e.g. 0.92 becomes 0.9199999999999999). Round to 6
// decimals on every percent/decimal conversion so user-facing values stay
// stable.
const ROUND6 = 1_000_000;
export const round6 = (n) => {
  if (n === null || n === undefined || n === "" || isNaN(n)) return 0;
  return Math.round(Number(n) * ROUND6) / ROUND6;
};
// Convert a user-typed percent value (e.g. "1.02") to a decimal (0.0102).
export const pctToDec = (p) => {
  if (p === null || p === undefined || p === "" || isNaN(Number(p))) return 0;
  return round6(Number(p) / 100);
};
// Convert a stored decimal (0.0102) back to a percent string ("1.02") for display.
export const decToPct = (d) => {
  if (d === null || d === undefined || d === "") return "";
  // Round to 4 decimals at the percent scale (= 6 decimals at the dec scale)
  // so the input box shows clean "1.02", not "1.0199999999999998".
  return (Math.round(Number(d) * 1_000_000) / 10_000).toString();
};
